import { TARGET_GROUP_NAME_MAX_LENGTH } from '@hprofits/api/target-group';
import { useCreateTargetGroup } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import { CampaignGroupId } from '@hprofits/shared';
import { MaybeRefOrGetter, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';

import { apiClient } from '@/api-client';
import { t } from '@/i18n';
import { nextNewName } from '@/modules/campaigns/components/Layout/composables';
import { justCreatedState } from '@/modules/campaigns/composables/useOnJustCreated';
import {
  getTargetGroupCreateGenericErrorMessage,
  getTargetGroupCreateMutationErrorOverride,
} from '@/modules/campaigns/utils/mutation-errors/targetGroup';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';
import type { UsePageControllerResult } from '@/modules/common/state/usePageController';
import { createMutationErrorHandler } from '@/modules/shared/composables';

// Persisted, not display copy: the name reaches the server as-is, so a localized base would leave
// one campaign holding target groups named in whichever language each was created in, and
// `nextNewName` would dedup "(2)" against a base it no longer recognises after a locale switch.
const TARGET_GROUP_DEFAULT_NAME = 'New Target';

export type UseCreateTargetGroupActionProps = Readonly<{
  campaignGroupId: CampaignGroupId;
  targetGroupNames: MaybeRefOrGetter<ReadonlyArray<string>>;
  pageController: UsePageControllerResult;
}>;

export type UseCreateTargetGroupActionResult = Readonly<{
  handleCreate: () => Promise<void>;
  isCreatePending: Readonly<Ref<boolean>>;
}>;

export const useCreateTargetGroupAction = ({
  campaignGroupId,
  targetGroupNames,
  pageController,
}: UseCreateTargetGroupActionProps): UseCreateTargetGroupActionResult => {
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();
  const mutation = useCreateTargetGroup(apiClient, SUPPRESS_ERROR_TOAST_META);

  const handleCreateError = createMutationErrorHandler({
    errors: pageController.errors,
    title: () => t('modules.campaigns.actions.errors.createTargetGroup'),
    override: getTargetGroupCreateMutationErrorOverride,
    genericMessage: getTargetGroupCreateGenericErrorMessage,
    logTag: '[useCreateTargetGroupAction]',
  });

  const handleCreate = async (): Promise<void> => {
    try {
      const { id } = await mutation.mutateAsync({
        campaignId: campaignGroupId,
        body: {
          name: nextNewName(TARGET_GROUP_DEFAULT_NAME, toValue(targetGroupNames), TARGET_GROUP_NAME_MAX_LENGTH),
          dimensions: {},
        },
      });
      const targetGroupRoute = routeResolver.targetGroup(campaignGroupId, id);
      await router.push({
        ...targetGroupRoute,
        state: justCreatedState(String(id)),
      });
    } catch (err) {
      handleCreateError(err);
      // Error is surfaced on the page's ErrorsPanel; no rethrow needed.
    }
  };

  return {
    handleCreate,
    isCreatePending: mutation.isPending,
  };
};
